import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "formulario-modelo" }
const _hoisted_2 = { class: "titulo-modelo" }
const _hoisted_3 = { key: 0 }
const _hoisted_4 = { class: "componente-titulo" }
const _hoisted_5 = { class: "peso" }
const _hoisted_6 = { class: "tabla-modelo" }
const _hoisted_7 = ["rowspan"]
const _hoisted_8 = ["rowspan"]
const _hoisted_9 = { key: 1 }

import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useModelo } from '@/store/GestionModelo'
import { useComponentes } from '@/store/GestionComponentes'
import { useGestionVersiones } from '@/store/GestionVersiones'
import { useGestionIndicadores } from '@/store/GestionIndicadores'
import { useGestionInstrumento } from '@/store/GestionInstrumentos'
import { useGestionIndicadorInstrumentos } from '@/store/GestionIndicadorInstrumentos'
import { useGestionTipoComponente } from '@/store/GestionTipoComponente'

import type Componente from '@/interfaces/Componente'
import type Instrumento from '@/interfaces/Instrumetos'
import { limpiarNiveles } from '@/utils/nivelesHelper'
import { podarArbolInactivo } from '@/utils/estructuraActiva'

interface FilaHorizontal {
  key: string
  nivel: number
  componentesRuta: string[]            
  componentesPesos: (number|null)[]    
  rowCounts: number[]                  
  indicadorNombre?: string
  indicadorPeso?: number | null
  instrumentoHerramienta?: string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ModeloCompleto',
  setup(__props) {

/**
 * Vista consolidada de un modelo con su estructura e indicadores.
 *
 * ── Enrutado ─────────────────────────────────────────────────────
 *   `ModeloCompleto/:id_modelo/:id_version` (ModeloCompleto) — permiso `modelos.ver`
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

const route = useRoute()
const idModelo = Number(route.params.id_modelo ?? NaN)
const idVersion = Number(route.params.id_version ?? NaN)

const storeModelo = useModelo()
const storeComponentes = useComponentes()
const storeVersion = useGestionVersiones()
const indicadoresStore = useGestionIndicadores()
const instrumentosStore = useGestionInstrumento()
const indicadorInstrumentoStore = useGestionIndicadorInstrumentos()
const tipoStore = useGestionTipoComponente()

const nombreModelo = ref('')
const componentes = ref<Componente[]>([])

const feedback = ref('')
const feedbackEsPositivo = ref(false)
const loadingInicial = ref(false)
const nivelesArray = ref<string[]>([])
const idComponenteRaiz = ref<number | null>(null)

// Tipo de fila horizontal con ruta completa de componentes
onMounted(async () => {
  if (!Number.isFinite(idModelo) || idModelo <= 0) {
    feedback.value = 'ID de modelo inválido.'
    feedbackEsPositivo.value = false
    return
  }

  loadingInicial.value = true
  try {
    // Todas las cargas independientes en paralelo
    const [modelo, raiz, activos] = await Promise.all([
      storeModelo.getModeloById(idModelo),
      storeComponentes.cargarRaizPorModelo(idModelo),
      storeVersion.getIdsComponentesActivos(idVersion),
      indicadoresStore.cargarTodos(),
      instrumentosStore.cargarTodos(),
      indicadorInstrumentoStore.getRelaciones(),
      tipoStore.getTipos()
    ])

    if (modelo?.niveles) nivelesArray.value = limpiarNiveles(modelo.niveles)
    nombreModelo.value = modelo?.nombre ?? ''

    if (!raiz) {
      feedback.value = 'No se encontró el componente raíz.'
      feedbackEsPositivo.value = false
      return
    }

    idComponenteRaiz.value = raiz.id_componente ?? null

    if (idComponenteRaiz.value != null) {
      const ok = await storeComponentes.cargarArbolDesdeRaiz(idComponenteRaiz.value!, nivelesArray.value)
      if (!ok) {
        feedback.value = 'No fue posible cargar la versión y sus componentes.'
        feedbackEsPositivo.value = false
      } else {
        const raizCompleta = storeComponentes.componentes.find(c => c.id_componente === idComponenteRaiz.value)
        if (raizCompleta?.children) {
          componentes.value = podarArbolInactivo(raizCompleta.children, activos)
        }
      }
    }

  } catch (e: any) {
    feedback.value = e?.message || 'Error al cargar datos iniciales.'
    feedbackEsPositivo.value = false
  } finally {
    loadingInicial.value = false
  }
})

// Helper para obtener instrumentos de un indicador
function instrumentosPorIndicadorFn(id_indicador: number): Instrumento[] {
  const relaciones = indicadorInstrumentoStore.relaciones.filter(r => r.id_indicador === id_indicador)
  return relaciones
    .map(r => instrumentosStore.instrumentos.find(i => i.id_instrumento === r.id_instrumento))
    .filter((i): i is Instrumento => !!i)
}

// Construye filas horizontales con ruta completa de componentes (nombre + peso + rowCounts)
function filasHorizontales(
  comp: Componente,
  nivel = 0,
  ruta: string[] = [],
  pesos: (number|null)[] = []
): FilaHorizontal[] {
  const filas: FilaHorizontal[] = []
  const nuevaRuta = [...ruta, comp.nombre]
  const nuevosPesos = [...pesos, comp.peso ?? null]

  const indicadores = indicadoresStore.indicadores.filter(i => i.id_componente === comp.id_componente)

  const baseFila = {
    componentesRuta: nuevaRuta,
    componentesPesos: nuevosPesos,
    rowCounts: [] as number[]
  }

  if (indicadores.length === 0) {
    filas.push({
      key: `comp-${comp.id_componente}-n${nivel}`,
      nivel,
      ...baseFila
    })
  } else {
    for (const ind of indicadores) {
      const instrumentos = instrumentosPorIndicadorFn(ind.id_indicador!)
      if (instrumentos.length === 0) {
        filas.push({
          key: `comp-${comp.id_componente}-ind-${ind.id_indicador}-n${nivel}`,
          nivel,
          ...baseFila,
          indicadorNombre: ind.enunciado,
          indicadorPeso: ind.peso_opinion ?? null
        })
      } else {
        for (const inst of instrumentos) {
          filas.push({
            key: `comp-${comp.id_componente}-ind-${ind.id_indicador}-inst-${inst.id_instrumento}-n${nivel}`,
            nivel,
            ...baseFila,
            indicadorNombre: ind.enunciado,
            indicadorPeso: ind.peso_opinion ?? null,
            instrumentoHerramienta: inst.herramienta
          })
        }
      }
    }
  }

  const hijos = comp.children ?? []
  for (const hijo of hijos) {
    filas.push(...filasHorizontales(hijo as Componente, nivel + 1, nuevaRuta, nuevosPesos))
  }

  // Calcular rowCounts: cuántas filas ocupa cada componente en cada nivel
  if (filas.length > 0) {
    const counts: number[] = nuevaRuta.map(name =>
      filas.filter(f => f.componentesRuta.includes(name)).length
    )
    filas.forEach(f => { f.rowCounts = counts })
  }

  return filas
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("h1", _hoisted_2, _toDisplayString(nombreModelo.value), 1),
    _cache[6] || (_cache[6] = _createElementVNode("div", { class: "divider" }, null, -1)),
    (componentes.value.length)
      ? (_openBlock(), _createElementBlock("section", _hoisted_3, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(componentes.value, (comp) => {
            return (_openBlock(), _createElementBlock("div", {
              key: comp.id_componente,
              class: "marco"
            }, [
              _createElementVNode("div", _hoisted_4, [
                _createElementVNode("h2", null, "Componente " + _toDisplayString(comp.ordinal) + " - " + _toDisplayString(comp.nombre), 1),
                _createElementVNode("span", _hoisted_5, "Peso: " + _toDisplayString(comp.peso ?? ''), 1)
              ]),
              _createElementVNode("table", _hoisted_6, [
                _createElementVNode("thead", null, [
                  _createElementVNode("tr", null, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(nivelesArray.value.length, (n) => {
                      return (_openBlock(), _createElementBlock(_Fragment, { key: n }, [
                        _createElementVNode("th", null, "Componente nivel " + _toDisplayString(n), 1),
                        _cache[0] || (_cache[0] = _createElementVNode("th", null, "Peso", -1))
                      ], 64))
                    }), 128)),
                    _cache[1] || (_cache[1] = _createElementVNode("th", null, "Indicador", -1)),
                    _cache[2] || (_cache[2] = _createElementVNode("th", null, "Peso", -1)),
                    _cache[3] || (_cache[3] = _createElementVNode("th", null, "Instrumento", -1))
                  ])
                ]),
                _createElementVNode("tbody", null, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filasHorizontales(comp), (fila, filaIndex) => {
                    return (_openBlock(), _createElementBlock("tr", {
                      key: fila.key
                    }, [
                      (filaIndex === 0)
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                            _createElementVNode("td", {
                              rowspan: filasHorizontales(comp).length,
                              class: "col-padre"
                            }, _toDisplayString(comp.nombre), 9, _hoisted_7),
                            _createElementVNode("td", {
                              rowspan: filasHorizontales(comp).length,
                              class: "col-padre"
                            }, _toDisplayString(comp.peso ?? ''), 9, _hoisted_8)
                          ], 64))
                        : _createCommentVNode("", true),
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(fila.componentesRuta.slice(1), (nombre, idx) => {
                        return (_openBlock(), _createElementBlock(_Fragment, { key: idx }, [
                          _createElementVNode("td", null, _toDisplayString(nombre), 1),
                          _createElementVNode("td", null, _toDisplayString(fila.componentesPesos[idx+1] ?? ''), 1)
                        ], 64))
                      }), 128)),
                      _createElementVNode("td", null, _toDisplayString(fila.indicadorNombre || '-'), 1),
                      _createElementVNode("td", null, _toDisplayString(fila.indicadorPeso ?? '-'), 1),
                      _createElementVNode("td", null, _toDisplayString(fila.instrumentoHerramienta || '-'), 1)
                    ]))
                  }), 128))
                ])
              ]),
              _cache[4] || (_cache[4] = _createElementVNode("div", { class: "tabla-divider" }, null, -1))
            ]))
          }), 128))
        ]))
      : (_openBlock(), _createElementBlock("section", _hoisted_9, _cache[5] || (_cache[5] = [
          _createElementVNode("p", null, "Cargando información del modelo...", -1)
        ])))
  ]))
}
}

})